/**
 * 63.1n (the owner, after the APC loop: "respawning apcs and vehicles, probably also from some common stock, and probably
 * needs some other mechanic to gate it"), with GameConfig.vehicleStock: each side's vehicles come back from a finite
 * stock, on 65.23's pattern (core/grenadeStock.ts).
 * - Each side's stock is GameConfig.vehicleStock spares of every vehicle it fields, kind by kind (the bike, the 6×6, the
 *   technical); each kind's cap — the most out at once — is the count it fielded × VEHICLE_STOCK.capShare.
 * - Every vehicle remembers where it was first parked (`home`: its side's base). Destroyed, it joins its side's queue;
 *   VEHICLE_STOCK.delayS later its replacement is parked — at the side's held landing pad if it holds one on that planet,
 *   else at the fallen one's home — while the kind's stock lasts and fewer than the cap are out, and when nothing parked
 *   stands on the spot (it waits otherwise). A kind with no stock left drops its queue.
 * - The bots' loops take a parked vehicle as they find one (nothing bot-side).
 * Pure and deterministic (ticks, ids, the queue's order); with the switch off no side has a stock and nothing here runs.
 */
import { MORTAR } from '../constants/action';
import type { GameState, StructureState, VehicleKind, Vec3 } from '../types';
import { VEHICLE_STOCK } from '../constants/action';
import { PHYS_DT } from '../constants';
import { structureTeam, structureHealth } from './body';
import { initialSeat } from './seats';
import { structureFootprint } from './buildingScale';
import { landingRegionFor } from './installations';
import { fobsOn } from './fob';
import { webDistances } from './web';
import { arcDistance } from './gameState';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub, surfaceTangentFrame } from '../math';
import { dcos, dsin } from './dmath';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { finishUnpacks, startUnpack } from './motorPoolStock';

import { VEHICLE_KINDS } from '../constants/body';   // (69.10 step 4: the list lives in the constants — core/lighter.ts reads it without this module's ring through the motor pool)
export { VEHICLE_KINDS };
const isVehicle = (s: Pick<StructureState, 'type'>): s is StructureState & { type: VehicleKind } => (VEHICLE_KINDS as readonly string[]).includes(s.type);

export type VehicleEvent = { kind: 'vehicleReplaced'; structureId: number; type: VehicleKind; team: number; at: 'base' | 'pad' | 'fob' | 'pool' };   // 69.4 step 2b: 'fob'; 63.1n with 67.75: 'pool', unpacked at the motor pool

/** The setup's sizing: each side's stock and cap by kind, from the vehicles it fields; every vehicle's home is where it stands. */
export function withVehicleStocks(state: GameState): GameState {
  const spares = state.config.vehicleStock ?? 0;
  if (!(spares > 0)) return state;
  const stocks: Record<number, Partial<Record<VehicleKind, { left: number; cap: number }>>> = {};
  const structures = state.structures.map(s => {
    if (!isVehicle(s) || s.health <= 0) return s;
    const team = structureTeam(state, s);
    if (team === null) return s;
    const k = (stocks[team] ??= {});
    const e = (k[s.type] ??= { left: 0, cap: 0 });
    e.left += spares; e.cap += VEHICLE_STOCK.capShare;
    return { ...s, home: { surfaceDir: { ...s.surfaceDir }, heading: { ...(s.drive?.heading ?? surfaceTangentFrame(s.surfaceDir).tx) } } };
  });
  for (const k of Object.values(stocks)) for (const e of Object.values(k)) if (e) e.cap = Math.max(1, Math.floor(e.cap));
  return Object.keys(stocks).length ? { ...state, structures, vehicleStocks: stocks, vehicleRefits: [] } : state;
}

/** How many of a side's vehicles of a kind stand (alive) now. */
function out(state: Pick<GameState, 'structures' | 'players'>, team: number, type: VehicleKind): number {
  let n = 0;
  for (const s of state.structures) if (s.type === type && s.health > 0 && structureTeam(state, s) === team) n++;
  return n;
}

/**
 * Where a replacement is parked: beside an anchor (its footprint and the vehicle's apart, toward the fallen one's home,
 * facing away from it), else its home. The anchor: the side's held landing pad on that planet — or, with FOBs on (69.4
 * step 2b), its linked FOB nearest the front (the one the web reaches farthest from the HQ, `webDistances`) when that lies
 * farther along the web than the pad (the pad wins a tie, and stands at the base when the web does not reach it). A cut
 * FOB counts as none.
 */
function parkingFor(state: GameState, team: number, planetIndex: number, type: VehicleKind, home: { surfaceDir: Vec3; heading: Vec3 }): { dir: Vec3; heading: Vec3; at: 'base' | 'pad' | 'fob' } {
  const pad = landingRegionFor(state, team);
  const planet = state.planets[planetIndex];
  if (!planet) return { dir: home.surfaceDir, heading: home.heading, at: 'base' };
  const padS = pad && pad.planetIndex === planetIndex ? state.structures.find(s => s.planetIndex === planetIndex && s.health > 0 && vecDot(vecNormalize(s.surfaceDir), vecNormalize(pad.facing)) > 1 - 1e-12) : undefined;
  let anchor: { dir: Vec3; s?: StructureState; at: 'pad' | 'fob' } | null = pad && pad.planetIndex === planetIndex ? { dir: pad.facing, s: padS, at: 'pad' } : null;
  if (fobsOn(state.config)) {
    const far = webDistances(state, team);
    let best: StructureState | null = null, bestD = -1;
    for (const s of state.structures) {
      if (s.type !== 'fob' || s.health <= 0 || s.planetIndex !== planetIndex || structureTeam(state, s) !== team) continue;
      const d = far.get(s.id);
      if (d !== undefined && (d > bestD || (d === bestD && best !== null && s.id < best.id))) { best = s; bestD = d; }
    }
    const padD = anchor ? (padS ? far.get(padS.id) ?? 0 : 0) : -Infinity;
    if (best && bestD > padD) anchor = { dir: best.surfaceDir, s: best, at: 'fob' };
  }
  if (!anchor) return { dir: home.surfaceDir, heading: home.heading, at: 'base' };
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const apart = (anchor.s ? structureFootprint(anchor.s, state.config, planet.radius) : 0) + structureFootprint({ type }, state.config, planet.radius) + person;
  const n = vecNormalize(anchor.dir);
  const toward = vecSub(vecNormalize(home.surfaceDir), vecScale(n, vecDot(vecNormalize(home.surfaceDir), n)));
  const t = vecLength(toward) > 1e-9 ? vecNormalize(toward) : surfaceTangentFrame(n).tx;
  const th = apart / planet.radius;
  const dir = vecNormalize(vecAdd(vecScale(n, dcos(th)), vecScale(t, dsin(th))));
  return { dir, heading: t, at: anchor.at };
}

/** Does a standing body (a vehicle, a structure) take up the spot a vehicle of `type` would be parked on? (A wreck does not: a replacement parks over its own.) */
function spotTaken(state: GameState, planetIndex: number, dir: Vec3, type: VehicleKind): boolean {
  const planet = state.planets[planetIndex];
  if (!planet) return true;
  const mine = structureFootprint({ type }, state.config, planet.radius);
  return state.structures.some(s => s.planetIndex === planetIndex && s.health > 0
    && arcDistance(s.surfaceDir, dir, planet.radius) < mine + structureFootprint(s, state.config, planet.radius));
}

/**
 * One tick of the stock: newly destroyed vehicles join the queue (their `home` taken off them, so each is queued once);
 * the due ones whose kind has stock left and room under its cap are parked where nothing stands, in the queue's order.
 * With a side's motor pool (GameConfig.motorPool, core/motorPoolStock.ts) a due one is unpacked there instead — on the
 * side's auto order, one at a time on its pad (an unpack done stands its vehicle there first); on its `order` the queue
 * waits for the commander. Null when nothing changed.
 */
export function stepVehicleStock(state0: GameState, tick: number): { state: GameState; events: VehicleEvent[] } | null {
  if (!state0.vehicleStocks) return null;
  // 63.1n with 67.75: the unpacks done this tick stand their vehicles on the pools' pads
  const unpacked = state0.motorPools ? finishUnpacks(state0, tick) : null;
  const state = unpacked?.done.length ? unpacked.state : state0;
  const poolEvents: VehicleEvent[] = (unpacked?.done ?? []).map(d => ({ kind: 'vehicleReplaced', structureId: d.structureId, type: d.type, team: d.team, at: 'pool' }));
  let structures: StructureState[] | null = null;
  let refits = state.vehicleRefits ?? [];
  let added = false;
  // the fallen: queued once (their home taken off), in id order
  state.structures.forEach((s, i) => {
    if (!isVehicle(s) || s.health > 0 || !s.home) return;
    const team = structureTeam(state, s);
    (structures ??= state.structures.slice())[i] = { ...s, home: undefined };
    if (team === null) return;
    if (!added) { refits = refits.slice(); added = true; }
    refits.push({ team, type: s.type, due: tick + Math.round(VEHICLE_STOCK.delayS / PHYS_DT), planetIndex: s.planetIndex, home: s.home, ownerId: s.ownerId, ...(s.armament ? { armament: s.armament } : {}) });   // (69.21: its variant kept)
  });
  let next: GameState = structures || added ? { ...state, ...(structures ? { structures } : {}), vehicleRefits: refits } : state;
  if (!refits.some(r => r.due <= tick)) return next === state0 ? null : { state: next, events: poolEvents };
  const events: VehicleEvent[] = [...poolEvents];
  let stocks = next.vehicleStocks!;
  const keep: typeof refits = [];
  for (const r of refits) {
    const stock = stocks[r.team]?.[r.type];
    if (!stock || stock.left <= 0) continue;   // none left of its kind: it is not coming
    const pool = next.motorPools?.[r.team];
    if (pool) {
      // 63.1n with 67.75: due, on the side's auto order, its frame unpacked on the pool's pad (busy or all out: it waits)
      if (r.due > tick || pool.order) { keep.push(r); continue; }
      const u = startUnpack({ ...next, vehicleStocks: stocks }, r.team, r.type, tick, r.ownerId, r.home, r.armament);
      if ('refused' in u) { keep.push(r); continue; }
      next = u.state; stocks = next.vehicleStocks!;
      continue;
    }
    if (r.due > tick || out(next, r.team, r.type) >= stock.cap) { keep.push(r); continue; }
    const park = parkingFor(next, r.team, r.planetIndex, r.type, r.home);
    if (spotTaken(next, r.planetIndex, park.dir, r.type)) { keep.push(r); continue; }
    const id = next.structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
    const vehicle: StructureState = {
      id, type: r.type, planetIndex: r.planetIndex, surfaceDir: park.dir, health: structureHealth(r.type), ownerId: r.ownerId,
      seats: initialSeat(r.type, r.armament, next.config.tankStowage), drive: { heading: park.heading, speed: 0, variant: 'gas' }, ...(r.armament ? { armament: r.armament } : {}), ...(r.armament === 'mortar' ? { mortar: { rounds: next.config.mortarStore ?? MORTAR.store } } : {}),   // (69.23: a new tube's store)
      home: r.home,   // its base, wherever it is parked: replaced in turn
    };
    stocks = { ...stocks, [r.team]: { ...stocks[r.team], [r.type]: { ...stock, left: stock.left - 1 } } };
    next = { ...next, structures: [...next.structures, vehicle], vehicleStocks: stocks };
    events.push({ kind: 'vehicleReplaced', structureId: id, type: r.type, team: r.team, at: park.at });
  }
  next = { ...next, vehicleRefits: keep };
  return { state: next, events };
}
